import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { faqData } from '../../utils/faqData';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map(({ question, answer }) => ({
    '@type': 'Question',
    name: question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: answer
    }
  }))
};

export default function FAQPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <script type="application/ld+json" data-testid="faq-jsonld">
        {JSON.stringify(faqSchema).replace(/</g, '\\u003c')}
      </script>
      <Navbar />
      <main>
        <section className="relative min-h-[350px] flex items-end overflow-hidden" aria-labelledby="faq-title">
          <div className="absolute inset-0">
            <img
              src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j2wu35i4_sadasf.png"
              alt="Etiteks woven labels produced in Istanbul"
              data-testid="faq-hero-image"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/55" />
          </div>
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 pb-12 pt-32">
            <h1 id="faq-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="faq-title">
              Frequently Asked Questions
            </h1>
            <p className="text-base md:text-lg text-gray-200 max-w-2xl" data-testid="faq-subtitle">
              Label manufacturing, ordering and delivery.
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16" aria-label="Frequently asked questions" data-testid="faq-list">
          <div className="max-w-3xl mx-auto px-6">
            <Accordion type="single" collapsible defaultValue="faq-1" className="border-t border-gray-200" data-testid="faq-accordion">
              {faqData.map((item, index) => (
                <AccordionItem key={item.question} value={`faq-${index + 1}`} className="border-gray-200" data-testid={`faq-item-${index + 1}`}>
                  <AccordionTrigger
                    className="gap-4 py-5 text-sm md:text-base leading-relaxed text-gray-900 hover:text-teal-700 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-teal-700"
                    data-testid={`faq-question-${index + 1}`}
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent forceMount data-ssg-faq-answer="" className="pb-6 pr-6 text-sm md:text-base leading-relaxed text-gray-600 break-words" data-testid={`faq-answer-${index + 1}`}>
                    <p>{item.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        <section className="py-12 md:py-16 border-t border-gray-200" data-testid="faq-contact-section">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4" data-testid="faq-contact-title">Still have questions?</h2>
            <p className="text-sm md:text-base text-gray-600 mb-8" data-testid="faq-contact-description">Send us your project details and we'll get back to you within 24 hours.</p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-8 py-3 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg transition-colors"
              data-testid="faq-contact-btn"
            >
              Contact Us <ArrowRight size={18} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}